/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback } from 'react';
import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, FieldValues, SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ContactJsonldTiersReadNoteGroupTimestampableBlameambleGroup } from '@europrocurement/l2d-domain/openApi/ApiTiers';
import { useSelector } from 'react-redux';
import { SelectOptions } from '@europrocurement/l2d-modules/modules/Offres/forms/types';
import SelectField from '@europrocurement/l2d-modules/modules/Offres/components/FormInputs/SelectField';
import {
    ContactTiersRead,
    useSociete,
    useTiersService,
    Utilisateur,
} from '@europrocurement/l2d-domain';
import { ModalContext } from '@europrocurement/flexy-components';
import { OptionsSelector } from '../../../redux';
import { PrescriberFormProps } from './common';
import {
    ContactFormValues,
    contactSchema,
    formToContactCreationPayload,
    formToContactUpdatePayload,
    getContactFormInitialValues,
    formPhonesToPayload,
    formEmailsToPayload,
} from './form';
import OptionalContactList from './OptionalContactList';

export type ContactFormProps = PrescriberFormProps & {
    contact?: ContactJsonldTiersReadNoteGroupTimestampableBlameambleGroup;
    /** Fonction pour résoudre les informations utilisateur */
    resolveUserInfo: (email: string) => Promise<Utilisateur | null>;
};

const ContactForm: React.FunctionComponent<ContactFormProps> = function (props) {
    const { afterSubmit, contact, prescriber, resolveUserInfo } = props;
    const { civilities } = useSelector(OptionsSelector);
    const prescriberId = prescriber?.id;
    const contactId = contact?.id;
    const isCreationForm = !contact;

    const { prescribersApi, contactsApi } = useTiersService();
    const { modalActions } = React.useContext(ModalContext);

    const theme = useTheme();
    const { breakpoints } = theme;
    const mdBreakpoint = useMediaQuery(breakpoints.down('md'));
    const xIdSociete = useSociete();

    const formContext = useForm({
        mode: 'onTouched',
        defaultValues: getContactFormInitialValues(contact),
        resolver: zodResolver(contactSchema),
    });

    const {
        control,
        watch,
        formState: { errors },
    } = formContext;

    /**
     * Proceed contact update
     *
     * The contact update call three different api endpoints
     * - 1. Contact main informations
     * - 2. Contact phones
     * - 3. Contact emails
     *
     * @param formValues - Values provided by contact form
     */
    const updateContact: SubmitHandler<ContactFormValues> = async (formValues) => {
        const canUpdate = typeof contactId !== 'undefined' && typeof prescriberId !== 'undefined';

        if (!canUpdate) {
            return;
        }
        const contactPayload = formToContactUpdatePayload(formValues);
        const phonesPayload = formPhonesToPayload(contact as ContactTiersRead, formValues);
        const emailsPayload = formEmailsToPayload(contact as ContactTiersRead, formValues);

        const promises = [
            contactsApi.updateContactContactsItem({
                xIdSociete,
                id: contactId.toString(),
                contactsContactJsonldContactUpdate: contactPayload,
            }),
            contactsApi.updateTelephonesContactPrescripteurContactsItem({
                xIdSociete,
                id: contactId.toString(),
                tiers: prescriberId.toString(),
                contactsContactJsonldContactWriteTelsPrescripteur: phonesPayload,
            }),
            contactsApi.updateEmailsContactPrescripteurContactsItem({
                xIdSociete,
                id: contactId.toString(),
                tiers: prescriberId.toString(),
                contactsContactJsonldContactWriteEmailsPrescripteur: emailsPayload,
            }),
        ];

        Promise.allSettled(promises).then((_results) => {
            afterSubmit();
            formContext.reset();
        });
    };

    const createContact: SubmitHandler<ContactFormValues> = async (formValues) => {
        const payload = formToContactCreationPayload(contact as ContactTiersRead, formValues);
        const canCreate = prescriber && prescriberId;
        if (canCreate) {
            await prescribersApi.createPrescripteurContactContactsCollection({
                xIdSociete,
                tiers: prescriberId,
                contactsContactJsonldContactCreate: payload,
            });
            afterSubmit();
            formContext.reset();
        }
    };

    /**
     * Submit the contact form
     */
    const submitForm: SubmitHandler<ContactFormValues> = (formValues: ContactFormValues) => {
        if (isCreationForm) {
            createContact(formValues);
        } else {
            updateContact(formValues);
        }
    };

    // RENDER / Structure & style

    /**
     * Display grid rules :
     * gridLine : always one line
     * gridHalf = : half line for high resolutions | one line for low resolution
     */
    const gridLine = {
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
    };

    const gridHalf = {
        ...gridLine,
        md: 6,
        lg: 6,
    };

    const formHeader = {
        type: 'customItem' as const,
        sx: {
            marginTop: '0px',
        },
        name: 'info',
        renderField: () => (
            <Typography
                fontWeight={500}
                fontSize="24px"
            >
                Informations du contact
            </Typography>
        ),
    };

    const horizontalDivider = {
        type: 'customItem' as const,
        name: 'horizontalDivider',
        renderField: () => (
            <Box sx={{ marginY: '32px', opacity: 0.3 }}>
                <hr />
            </Box>
        ),
    };

    const spacer = {
        type: 'customItem' as const,
        name: 'spacer',
        renderField: () => <Box sx={{ marginY: '20px' }} />,
    };

    /**
     * Civilities options formatted for select
     */
    const civilitiesOptions: SelectOptions = [
        ...civilities.map((apiCivility) => ({
            label: apiCivility.libelle ?? '( Aucune valeur )',
            value: apiCivility.id,
        })),
        { label: 'Non renseignée', value: 0 },
    ];

    const generalInformationsSection: FormStructure[] = [
        {
            type: 'customItem',
            name: 'civilityId',
            ...gridLine,
            renderField: () => (
                <Controller
                    name="civilityId"
                    control={control}
                    render={({ field }) => (
                        <SelectField
                            {...field}
                            value={watch(field.name)}
                            name={field.name}
                            label="Civilité"
                            options={civilitiesOptions}
                            errors={errors}
                        />
                    )}
                />
            ),
        },
        {
            type: 'text',
            name: 'lastName',
            inputlabel: 'Nom',
            rules: {
                required: 'Vous devez renseigner un nom',
            },
            ...gridHalf,
        },
        {
            type: 'text',
            name: 'firstName',
            inputlabel: 'Prénom',
            rules: {
                required: 'Vous devez renseigner un prénom',
            },
            ...gridHalf,
        },
        {
            type: 'text',
            name: 'position',
            inputlabel: 'Fonction',
            ...gridLine,
        },
    ];

    /** Section that contains inputs for phones and emails */
    const contactsSection: FormStructure[] = [
        {
            type: 'text',
            name: 'mainPhone',
            inputlabel: 'Téléphone principal',
            rules: {
                required: 'Vous devez renseigner un numéro de téléphone',
            },
            ...gridLine,
        },
        {
            type: 'customItem',
            name: 'optionalPhones',
            ...gridLine,
            renderField: () => (
                <Box>
                    <OptionalContactList
                        type="phone"
                        context={formContext}
                    />
                </Box>
            ),
        },
        {
            type: 'text',
            name: 'mainEmail',
            inputlabel: 'E-mail principal',
            rules: {
                required: 'Vous devez renseigner un e-mail',
            },
            ...gridLine,
        },
        {
            type: 'customItem',
            name: 'optionalEmails',
            ...gridLine,
            renderField: () => (
                <Box sx={{ marginTop: '7px' }}>
                    <OptionalContactList
                        type="email"
                        context={formContext}
                    />
                </Box>
            ),
        },
    ];

    const contactSettingsSection: FormStructure[] = [
        {
            type: 'selectItems',
            name: 'isAdmin',
            mode: 'inline',
            inputlabel: 'Rôle',
            colInline: 6,
            items: [
                {
                    label: 'Administrateur',
                    value: true,
                },
                {
                    label: 'Collaborateur',
                    value: false,
                },
            ],
            multiple: false,
            rules: {},
        },
        spacer,
        {
            type: 'boolean',
            name: 'isMainContact',
            inputlabel: 'Contact principal',
            sx: { marginTop: '15px' },
            ...gridLine,
        },
        spacer,
        {
            type: 'boolean',
            name: 'isBillingContact',
            inputlabel: 'Contact comptabilité',
            ...gridLine,
        },
        spacer,
        {
            type: 'boolean',
            name: 'hasLeft',
            inputlabel: 'À quitté',
            inputSublabel: 'Le contact à quitté la société',
            display: !isCreationForm,
            ...gridLine,
        },
    ];

    const formStructure: FormStructure[] = [
        formHeader,
        ...generalInformationsSection,
        horizontalDivider,
        ...contactsSection,
        horizontalDivider,
        ...contactSettingsSection,
    ];

    const wrapperStyle = {
        maxWidth: mdBreakpoint ? '100%' : '60%',
        margin: 'auto',
    };

    const submitLabel = isCreationForm ? 'Créer un nouveau contact' : 'Mettre à jour le contact';

    return (
        <Box sx={wrapperStyle}>
            <FlexyForm
                formContext={formContext as unknown as UseFormReturn<FieldValues>}
                formStructure={formStructure}
                onSubmit={submitForm}
                submitButton={{
                    render: ({ isSubmitSuccessful, isSubmitting, isLoading, isValidating }) => (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                gap: '32px',
                                paddingTop: '32px',
                            }}
                        >
                            <Button
                                type="button"
                                variant="contained"
                                sx={{
                                    backgroundColor: 'transparent',
                                    color: theme.palette.primary.main,
                                    ':hover': { backgroundColor: 'transparent' },
                                }}
                                onClick={() => modalActions.close()}
                            >
                                Annuler
                            </Button>
                            <Button
                                type="submit"
                                disabled={
                                    isSubmitSuccessful || isSubmitting || isLoading || isValidating
                                }
                                variant="contained"
                            >
                                {submitLabel}
                            </Button>
                        </Box>
                    ),
                }}
            />
        </Box>
    );
};

export default ContactForm;
